import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CountryDatalist } from "@/components/LocationInput";

const CityRow = () => {
  const [cities, setCities] = useState([]);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/cities").then((r) => setCities(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post("/admin/cities", { name, country });
      toast.success(`${name}, ${country} added`);
      setName(""); setCountry("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/cities/${editing.id}`, { name: editing.name, country: editing.country });
      toast.success("City updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="silk-card rounded-lg p-5 mb-8" data-testid="admin-cities">
      <p className="font-display text-xl text-foreground mb-1">Cities</p>
      <p className="text-xs text-muted-foreground mb-4">Add a city anywhere in the world and practitioners can list there instantly. Click a city to fix its name or country.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {cities.map((c) => (
          <Badge key={c.id} variant="secondary" data-testid={`admin-city-${c.id}`}
            onClick={() => setEditing({ id: c.id, name: c.name, country: c.country })}
            className="font-normal cursor-pointer hover:border-primary/50 gap-1.5">
            {c.name}, {c.country} <Pencil size={10} className="opacity-60" />
          </Badge>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
        <Input data-testid="city-name-input" placeholder="City name (e.g. Edmonton)" value={name} onChange={(e) => setName(e.target.value)} />
        <Input data-testid="city-country-input" placeholder="Country (e.g. Canada)" list="orec-countries" value={country} onChange={(e) => setCountry(e.target.value)} />
        <CountryDatalist />
        <Button size="sm" data-testid="city-add-btn" className="rounded-full px-5 shrink-0 sm:self-auto self-start" disabled={!name.trim() || !country.trim()} onClick={add}>Add</Button>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit city</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">City name</Label>
                <Input data-testid="edit-city-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Country</Label>
                <Input data-testid="edit-city-country-input" value={editing.country} list="orec-countries" onChange={(e) => setEditing({ ...editing, country: e.target.value })} className="mt-2" />
              </div>
              <p className="text-xs text-muted-foreground">Fixing a country updates every practitioner and member already linked to this city.</p>
              <Button data-testid="edit-city-save-btn" onClick={saveEdit} disabled={!editing.name.trim() || !editing.country.trim()} className="rounded-full px-6">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CategoryForm = ({ initial, onSaved }) => {
  const [name, setName] = useState(initial?.name || "");
  const [subs, setSubs] = useState((initial?.subcategories || []).map((s) => s.name).join(", "));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const payload = { name, icon: initial?.icon || "sparkles", subcategories: subs.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (initial) await api.put(`/admin/categories/${initial.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Category saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category name</Label>
        <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sub-categories (comma separated)</Label>
        <Input data-testid="category-subs-input" value={subs} onChange={(e) => setSubs(e.target.value)} placeholder="Reiki, Tarot Reading, …" className="mt-2" />
      </div>
      <Button data-testid="category-save-btn" onClick={save} disabled={busy || !name} className="rounded-full px-6">Save</Button>
    </div>
  );
};

export const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/categories").then((r) => setCategories(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Category removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <CityRow />
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-lg md:text-2xl text-foreground">Categories</h2>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-category-btn" className="rounded-full"><Plus size={14} className="mr-1" /> New category</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
            <CategoryForm initial={editing} onSaved={() => { setOpen(false); setEditing(null); load(); }} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3" data-testid="admin-categories-list">
        {categories.map((c) => (
          <div key={c.id} className="silk-card rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-xl text-foreground">{c.name}</p>
              <div className="flex gap-3">
                <button data-testid={`edit-category-${c.id}`} onClick={() => { setEditing(c); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {c.subcategories?.map((s) => <Badge key={s.id} variant="secondary" className="font-normal">{s.name}</Badge>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
